import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useRef } from 'react';
import accepts from 'attr-accept';
import classnames from 'classnames';
import { Flex, Body } from '@cognite/cogs-core';
import { FileUploadIcon } from '@cognite/cogs-icons';
import { getThemeClassNames, IconWrapper } from '@cognite/cogs-utils';
import { Dropzone } from '../dropzone/dropzone.js';
import { SingleUploadedFile, MultipleUploadedTable } from './elements.js';
import { convertFileListToFileArray } from './utils.js';

/**
 * FileUpload is a component that allows users to upload files to the application.
 */
var FileUploadInner = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var className = _a.className,
    inverted = _a.inverted,
    inputId = _a.id,
    description = _a.description,
    _a$title = _a.title,
    title = _a$title === undefined ? 'Drag & drop files here or click to select' : _a$title,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    _a$multiple = _a.multiple,
    multiple = _a$multiple === undefined ? false : _a$multiple,
    accept = _a.accept,
    _a$disabled = _a.disabled,
    disabled = _a$disabled === undefined ? false : _a$disabled,
    onChange = _a.onChange,
    _a$required = _a.required,
    required = _a$required === undefined ? false : _a$required,
    error = _a.error,
    _a$isValid = _a.isValid,
    isValid = _a$isValid === undefined ? true : _a$isValid,
    name = _a.name,
    otherProps = __rest(_a, ["className", "inverted", "id", "description", "title", "size", "multiple", "accept", "disabled", "onChange", "required", "error", "isValid", "name"]);
  var inputRef = useRef(null);
  var onButtonClick = function onButtonClick() {
    var _a;
    (_a = inputRef.current) === null || _a === undefined ? undefined : _a.click();
  };
  var handleDrop = function handleDrop(e) {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      var files = convertFileListToFileArray(e.dataTransfer.files);
      // if any files have invalid extensions, return
      if (accept) {
        var hasInvalidFiles = files.some(function (file) {
          return !accepts(file, accept);
        });
        if (hasInvalidFiles) {
          return;
        }
      }
      onChange === null || onChange === undefined ? undefined : onChange(e, files);
    }
  };
  // triggers when file is selected with click
  var handleChange = function handleChange(e) {
    e.preventDefault();
    var files = convertFileListToFileArray(e.target.files);
    onChange === null || onChange === undefined ? undefined : onChange(e, files);
  };
  return jsxs(Dropzone, Object.assign({
    ref: ref,
    overlayComponent: jsx("div", {
      className: "cogs-lab-fileupload-drag-overlay"
    }),
    onDrop: handleDrop,
    onSubmit: function onSubmit(e) {
      return e.preventDefault();
    }
  }, otherProps, {
    children: [jsx("input", Object.assign({
      disabled: disabled,
      id: inputId,
      accept: accept,
      ref: inputRef,
      className: "cogs-visually-hidden cogs-lab-fileupload-input",
      type: "file",
      multiple: multiple,
      onChange: handleChange,
      name: name
    }, otherProps)), jsx("label", {
      className: classnames('cogs-visually-hidden cogs-lab-fileupload-label'),
      htmlFor: inputId,
      children: "Drag & drop files here or click to select"
    }), jsxs("button", {
      "aria-controls": inputId,
      type: "button",
      disabled: disabled,
      onClick: onButtonClick,
      "aria-describedby": "file-upload-info",
      className: classnames.apply(undefined, ['cogs-lab-fileupload'].concat(_toConsumableArray(getThemeClassNames(inverted)), [disabled && 'cogs-lab-fileupload-disabled', size === 'small' && 'cogs-lab-fileupload-small', !isValid && 'cogs-lab-fileupload--invalid', className])),
      children: [jsx(IconWrapper, {
        size: 24,
        className: "cogs-lab-fileupload__icon-wrapper",
        children: jsx(FileUploadIcon, {
          width: "24px",
          height: "24px"
        })
      }), jsxs(Flex, {
        direction: "column",
        children: [jsxs(Body, {
          className: "cogs-lab-fileupload-title",
          size: "small",
          strong: true,
          children: [title, ' ', required ? jsx("span", {
            className: "cogs-lab-fileupload-title-asterisk",
            children: "*"
          }) : '']
        }), jsx("span", {
          id: "file-upload-info",
          className: "cogs-lab-fileupload-description cogs-body-x-small",
          children: description || "Supported files: ".concat(accept || '.jpg, .jpeg, .png, .mp4, .webm')
        }), jsx("span", {
          className: "cogs-lab-fileupload__error-message",
          children: error
        })]
      })]
    })]
  }));
});
var FileUpload = Object.assign(FileUploadInner, {
  displayName: 'FileUpload',
  UploadedItem: SingleUploadedFile,
  UploadedItems: MultipleUploadedTable
});

export { FileUpload, FileUploadInner };
